import { useEffect, useMemo, useState, useRef } from "react";
import { ActionIcon, Center, Flex, Loader, Title, Tooltip, useMantineTheme } from "@mantine/core";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_SortingState,
  type MRT_ColumnFiltersState,
} from "mantine-react-table";
import { IconEye } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useDebouncedValue } from "@mantine/hooks";

type CollaborationListItem = {
  id: string;
  requesterCompanyName: string | null;
  receiverCompanyName: string | null;
  acceptedDate: string;
};

const PAGE_SIZE = 10;

export default function AdminCollaborationListPage() {
  const navigate = useNavigate();
  const theme = useMantineTheme();

  const [collaborations, setCollaborations] = useState<CollaborationListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [debouncedFilters] = useDebouncedValue(columnFilters, 400);

  const previousParamsRef = useRef<string>("");

  const filterParams = useMemo(() => {
    const filtered = debouncedFilters.filter((f) => f.value !== undefined && f.value !== null && f.value !== "");
    return Object.fromEntries(filtered.map((f) => [f.id, f.value]));
  }, [debouncedFilters]);

  useEffect(() => {
    const params = {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      sorting: sorting.map((s) => ({ id: s.id, desc: s.desc })),
    };

    const paramsString = JSON.stringify({ ...params, filterParams });

    if (paramsString === previousParamsRef.current) {
      return;
    }

    previousParamsRef.current = paramsString;

    const fetchCollaborations = async () => {
      setLoading(true);
      try {
        // Build filter only with non-empty values
        const filter: Record<string, any> = {};
        if (filterParams.requesterCompanyName) {
          filter.requesterCompanyName = filterParams.requesterCompanyName;
        }
        if (filterParams.receiverCompanyName) {
          filter.receiverCompanyName = filterParams.receiverCompanyName;
        }

        const res = await trpc.admin.getCollaborationList.query({
          page: params.page,
          limit: params.limit,
          filter: Object.keys(filter).length > 0 ? filter : undefined,
          sorting: params.sorting.length > 0 ? params.sorting : undefined,
        });

        setCollaborations(
          res.collaborations.map((c: any) => ({
            id: c.id,
            requesterCompanyName: c.requesterCompanyName,
            receiverCompanyName: c.receiverCompanyName,
            acceptedDate: c.acceptedDate,
          }))
        );
        setTotalRows(res.meta.total);
      } catch (err) {
        handleTrpcError(err);
        setCollaborations([]);
        setTotalRows(0);
      } finally {
        setLoading(false);
      }
    };

    fetchCollaborations();
  }, [pagination.pageIndex, pagination.pageSize, filterParams, sorting]);

  useEffect(() => {
    if (pagination.pageIndex !== 0) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [filterParams]);

  const columns: MRT_ColumnDef<CollaborationListItem>[] = useMemo(
    () => [
      {
        accessorKey: "requesterCompanyName",
        header: "Requester Company",
        size: 250,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string | null;
          return value || "—";
        },
      },
      {
        accessorKey: "receiverCompanyName",
        header: "Requestee Company",
        size: 250,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string | null;
          return value || "—";
        },
      },
      {
        accessorKey: "acceptedDate",
        header: "Collab Date",
        size: 180,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string;
          if (!value) return <span style={{ color: "#94a3b8" }}>—</span>;
          return new Date(value).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          });
        },
      },
    ],
    [navigate]
  );

  if (loading && collaborations.length === 0) {
    return (
      <Center h="50vh">
        <Loader size="lg" variant="dots" />
      </Center>
    );
  }

  return (
    <Flex direction="column" w="100%" p="xl">
      <Title order={2} fw={400} mb="xl">
        Collaborations
      </Title>

      <MantineReactTable
        columns={columns}
        data={collaborations}
        enableRowNumbers
        enableStickyHeader
        enableColumnPinning
        enableRowActions
        enableColumnResizing
        columnResizeMode="onEnd"
        layoutMode="semantic"
        manualPagination
        manualFiltering
        manualSorting
        enableGlobalFilter={false}
        rowCount={totalRows}
        onPaginationChange={setPagination}
        onColumnFiltersChange={setColumnFilters}
        onSortingChange={setSorting}
        state={{
          pagination,
          columnFilters,
          sorting,
          isLoading: loading,
        }}
        positionActionsColumn="last"
        renderRowActions={({ row }) => (
          <Tooltip label="View">
            <ActionIcon
              c="#475569"
              variant="transparent"
              onClick={() => navigate(`/admin/collaborations/${row.original.id}`)}
            >
              <IconEye />
            </ActionIcon>
          </Tooltip>
        )}
        initialState={{
          columnPinning: { right: ["mrt-row-actions"] },
          density: "md",
        }}
        mantineTableBodyCellProps={{
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 200,
          },
        }}
        mantineTableHeadCellProps={{
          style: {
            fontWeight: 500,
            fontSize: "var(--mantine-font-size-md)",
            color: theme.colors.slate[6],
          },
        }}
        mantineTableContainerProps={{
          style: {
            maxHeight: "calc(100vh - 250px)",
            minHeight: "100px",
            width: "100%",
            overflowX: "auto",
          },
        }}
        renderEmptyRowsFallback={() => (
          <tr>
            <td colSpan={columns.length + 2} style={{ textAlign: "center", padding: "2rem" }}>
              No collaborations found. Try adjusting your filters.
            </td>
          </tr>
        )}
      />
    </Flex>
  );
}
